import { useMemo } from 'react';
import type { FieldErrors, FieldValues } from 'react-hook-form';
import { useIntl } from 'react-intl';
import type { ZodDiscriminatedUnionDef, ZodObjectDef, ZodRecordDef, ZodType } from 'zod';

import type { NestedPossibleZodEffect } from '../types';
import { isDiscriminatedUnionDef } from '../utils/isDiscriminatedUnionDef';
import { isObjectDef } from '../utils/isObjectDef';
import { isRecordDef } from '../utils/isRecordDef';
import { translateDiscriminatedUnionErrors, translateObjectErrors, translateRecordErrors } from '../utils/translateErrors';
import { unwrapPossibleZodEffects } from '../utils/unwrapPossibleZodEffects';

export type AcceptedZodType = ZodType<object, ZodObjectDef | ZodRecordDef | ZodDiscriminatedUnionDef<string>>;

export function useTranslateErrors<Schema extends NestedPossibleZodEffect<AcceptedZodType>>(schema: Schema) {
    const { formatMessage } = useIntl();

    return useMemo(() => {
        const innerSchema = unwrapPossibleZodEffects(schema);
        const def = innerSchema._def;

        if (isObjectDef(def)) {
            return (errors: FieldErrors) => translateObjectErrors(def, errors, { formatMessage });
        }
        if (isRecordDef(def)) {
            return (errors: FieldErrors) => translateRecordErrors(def, errors, { formatMessage });
        }

        if (isDiscriminatedUnionDef(def)) {
            return (errors: FieldErrors, values: FieldValues) => translateDiscriminatedUnionErrors(def, errors, values, { formatMessage });
        }

        throw new Error('invalid def received, ' + String(def));
    }, [schema, formatMessage]);
}
